import { useMemo } from 'react';
import type { OfflineQueueEntry, OfflineQueueKind } from '@/lib/offline/types';
import { useOfflineQueueStore } from '@/stores/offlineQueueStore';

interface OfflineQueueSlices {
  all: OfflineQueueEntry[];
  queued: OfflineQueueEntry[];
  retrying: OfflineQueueEntry[];
  synced: OfflineQueueEntry[];
}

export function useOfflineQueue(): OfflineQueueSlices {
  const entriesMap = useOfflineQueueStore((state) => state.entries);

  return useMemo(() => {
    const entries = Object.values(entriesMap);
    const sorted = [...entries].sort((a, b) => b.createdAt - a.createdAt);
    const queued = sorted.filter((entry) => entry.status === 'queued' || entry.status === 'processing');
    const retrying = sorted.filter((entry) => entry.status === 'retry');
    const synced = sorted.filter((entry) => entry.status === 'synced');

    return {
      all: sorted,
      queued,
      retrying,
      synced,
    };
  }, [entriesMap]);
}

export function useOfflineQueueByKind(kind: OfflineQueueKind): OfflineQueueSlices {
  const { all, queued, retrying, synced } = useOfflineQueue();

  return useMemo(() => ({
    all: all.filter((entry) => entry.meta.kind === kind),
    queued: queued.filter((entry) => entry.meta.kind === kind),
    retrying: retrying.filter((entry) => entry.meta.kind === kind),
    synced: synced.filter((entry) => entry.meta.kind === kind),
  }), [all, queued, retrying, synced, kind]);
}
